import React from 'react';
import { IoMdAdd } from 'react-icons/io';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { Input } from '@breeze-ai/ui-library';
import { Button, clsxMerge } from '@breezeai-frontend/cargo-ui';

import {
  useClaimCreationFlowContext,
  useClaimCreationStateAction,
} from '../context/hooks';
import { type ClaimContact } from '../context/types';
import { useClaimContactFormInputValidation } from '../form-validations';

const contactFields: (keyof ClaimContact)[] = ['name', 'email', 'phone'];

export const ContactDetailsForm = () => {
  const { formsData, formErrors } = useClaimCreationFlowContext();
  const { setFormData, setFormError } = useClaimCreationStateAction();
  const validateInput = useClaimContactFormInputValidation();

  const { contacts } = formsData;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-4">
        {contacts.map((contact, index) => {
          const doesRowHaveErrors =
            formErrors.contacts?.[index]?.name ||
            formErrors.contacts?.[index]?.email ||
            formErrors.contacts?.[index]?.phone;

          return (
            <React.Fragment key={contact.id}>
              <Input
                label="Contact name"
                placeholder="Who can we talk to?"
                required={true}
                value={contact.name}
                onChange={({ target: { value: contactName } }) =>
                  setFormData({
                    contacts: contacts.map((c, i) =>
                      i === index ? { ...c, name: contactName } : c,
                    ),
                  })
                }
                error={!!formErrors.contacts?.[index]?.name}
                errorHelperText={formErrors.contacts?.[index]?.name?.reason}
                validator={(value) => validateInput('name', index, value)}
                testId={`contact-name-input-${index}`}
              />
              <Input
                label="Contact email"
                placeholder="Who can we email?"
                type="email"
                required={true}
                value={contact.email}
                onChange={({ target: { value: contactEmail } }) =>
                  setFormData({
                    contacts: contacts.map((c, i) =>
                      i === index ? { ...c, email: contactEmail } : c,
                    ),
                  })
                }
                error={!!formErrors.contacts?.[index]?.email}
                errorHelperText={formErrors.contacts?.[index]?.email?.reason}
                validator={(value) => validateInput('email', index, value)}
                testId={`contact-email-input-${index}`}
              />
              <Input
                label="Contact phone"
                placeholder="+1 (111) 111-111"
                type="tel"
                required={true}
                value={contact.phone}
                onChange={({ target: { value: contactPhone } }) =>
                  setFormData({
                    contacts: contacts.map((c, i) =>
                      i === index ? { ...c, phone: contactPhone } : c,
                    ),
                  })
                }
                error={!!formErrors.contacts?.[index]?.phone}
                errorHelperText={formErrors.contacts?.[index]?.phone?.reason}
                validator={(value) => validateInput('phone', index, value)}
                testId={`contact-phone-input-${index}`}
              />
              <div
                className={clsxMerge(
                  'flex',
                  doesRowHaveErrors ? 'items-center' : 'items-end',
                )}
              >
                <Button
                  data-testid={`contact-delete-button-${index}`}
                  variant="ghost"
                  onPress={() => {
                    setFormData({
                      contacts: contacts.filter((_, i) => i !== index),
                    });

                    // Then shift errors for all subsequent rows up by one
                    const newErrors = formErrors.contacts.filter(
                      (_, i) => i !== index,
                    );

                    // Update each field's error state
                    newErrors.forEach((contactErrors, newIndex) => {
                      contactFields.forEach((field) => {
                        if (contactErrors[field]) {
                          setFormError({
                            field: 'contacts',
                            index: newIndex,
                            subField: field,
                            error: true,
                            reason: contactErrors[field]?.reason,
                          });
                        } else {
                          setFormError({
                            field: 'contacts',
                            index: newIndex,
                            subField: field,
                            error: false,
                          });
                        }
                      });
                    });
                  }}
                  isDisabled={contacts.length === 1}
                  customStyles="w-9 h-9 text-text-error"
                >
                  <RiDeleteBin6Line className="w-3 h-3" />
                </Button>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      <Button
        leftIcon={<IoMdAdd className="w-5 h-5" />}
        variant="ghost"
        onPress={() => {
          setFormData({
            contacts: [
              ...contacts,
              { id: crypto.randomUUID(), name: '', email: '', phone: '' },
            ],
          });

          // Initialize clean error state for the new row
          contactFields.forEach((field) => {
            setFormError({
              field: 'contacts',
              index: contacts.length,
              subField: field,
              error: false,
            });
          });
        }}
        customStyles="self-start"
        // eslint-disable-next-line i18next/no-literal-string -- TODO fix localization
      >
        Add contact
      </Button>
    </div>
  );
};
